import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { SpeechMarkup } from 'shared/src/types/speech-markup';
import type { RiveAnimationConfig } from 'shared/src/types/rive-animation-config';
import { AnalyticsService } from '../../../../../shared/src/services/analytics.service';
import { FunnelSampleContentService } from '../../../../../shared/src/services/funnel-sample-content.service';
import { fadeAnimation } from '../../animations/route-animations';
import { injectScreenIndex } from '../../flow/flow-routing';
import { FunnelFlowService } from '../../services/funnel-flow.service';
import { MentionedInComponent } from '../mentioned-in/mentioned-in.component';
import { TextPlayerComponent } from '../text-player/text-player.component';

@Component({
  selector: 'app-funnel-start',
  templateUrl: './start.component.html',
  styleUrls: ['./start.component.scss'],
  imports: [MatButtonModule, MentionedInComponent, TextPlayerComponent],
  animations: [fadeAnimation],
})
export class StartComponent implements OnInit, AfterViewInit {
  @ViewChild(TextPlayerComponent) textPlayer: TextPlayerComponent;

  fullText: string;
  audioFile: string;
  markups: SpeechMarkup[];

  // UI state for continue button
  isButtonEnabled = false;
  hasStartedPlayback = false;
  hasFinishedFlow = false;

  isBadgeShown = false;

  private readonly screenIndex = injectScreenIndex();

  private autoContinueTimer: any = null;
  private buttonEnableTimer: any = null;

  constructor(
    private flowService: FunnelFlowService,
    private analyticsService: AnalyticsService,
    private sampleContentService: FunnelSampleContentService
  ) {}

  ngOnInit() {
    const { text, markups, audioPath } = this.getPlayingText();

    this.fullText = text;
    this.markups = markups;
    this.audioFile = audioPath;
  }

  ngAfterViewInit(): void {
    this.analyticsService.track('web_intro_screen_view', {
      competitor: this.flowService.getCompetitor(),
    });
  }

  private getMainAnimation(): RiveAnimationConfig {
    return {
      fileName: 'confident',
      animations: ['dots', 'stars', 'char'],
      ratio: 1.7 / 1,
    };

    // fileName: 'rocket',
    // animations: ['char', 'rocket'],

    // return {
    //   fileName: 'impressed',
    //   animations: ['char', 'skirt', 'birds', 'wings'],
    //   ratio: 1.75 / 1,
    // };
  }

  /**
   * Sets the button enabled state
   */
  setButtonState(isEnabled: boolean): void {
    this.isButtonEnabled = isEnabled;
  }

  continueButtonClicked(): void {
    // If already started playback and button is enabled, proceed with flow
    if (this.hasStartedPlayback && this.isButtonEnabled) {
      this.clearAutoContinueTimer();
      this.clearButtonEnableTimer();
      this.hasFinishedFlow = true;
      this.analyticsService.track('web_intro_screen_viewed');
      this.flowService.handleFinishedScreen(this.screenIndex);

      return;
    }

    // If not started yet, start playback and disable button temporarily
    if (this.textPlayer && !this.hasStartedPlayback) {
      this.hasStartedPlayback = true;
      this.setButtonState(false);

      this.textPlayer.startPlayback();
    }
  }

  onAudioProgressed(progress: number): void {
    // For progress-based variant (original behavior), enable button at 40% progress
    if (!this.isButtonEnabled && progress >= 0.4) {
      this.setButtonState(true);
    }

    // If audio has completed or is very close to completion, set auto-continue
    if (progress >= 1 && !this.hasFinishedFlow) {
      this.setupAutoContinue();
    }
  }

  /**
   * Set up a timer to auto-continue if user doesn't click
   */
  private setupAutoContinue(): void {
    // Clear any existing timer first
    this.clearAutoContinueTimer();

    // Set new timer to auto-continue in 1 second
    this.autoContinueTimer = setTimeout(() => {
      if (!this.hasFinishedFlow) {
        this.continueButtonClicked();
      }
    }, 1000);
  }

  /**
   * Clear the auto-continue timer if set
   */
  private clearAutoContinueTimer(): void {
    if (this.autoContinueTimer) {
      clearTimeout(this.autoContinueTimer);
      this.autoContinueTimer = null;
    }
  }

  /**
   * Clear the button enable timer if set
   */
  private clearButtonEnableTimer(): void {
    if (this.buttonEnableTimer) {
      clearTimeout(this.buttonEnableTimer);
      this.buttonEnableTimer = null;
    }
  }

  get buttonText(): string {
    if (!this.isButtonEnabled && this.hasStartedPlayback) {
      return $localize`:@@playing:Playing...`;
    }
    if (this.hasStartedPlayback) {
      return $localize`:@@continue:Continue`;
    }
    return $localize`:@@getStarted:Get Started`;
  }

  private getPlayingText(): {
    text: string;
    markups: SpeechMarkup[];
    audioPath: string;
  } {
    const intro = this.sampleContentService.getIntroSpeech();

    return {
      text: intro.text,
      markups: intro.markups,
      audioPath: intro.audioPath,
    };
  }
}
